import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, slicePage, totalPagesFromCount } from "../lib/adminPagination";
import {
  adminCard,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminSearchWrap,
  adminStatusPill,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";
import { deriveAdminUiStatus } from "../lib/proposalContract";

const PAGE_SIZE = ADMIN_PAGE_SIZE;
const INACTIVE_BOOKING_STATUSES = new Set(["declined", "cancelled"]);

const bookingSortTime = (row) => {
  const stamp = row?.updated_at || row?.created_at;
  const parsed = stamp ? new Date(stamp) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : 0;
};

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const isInactiveBookingStatus = (row) => {
  const raw = String(row?.status || "").trim().toLowerCase();
  return INACTIVE_BOOKING_STATUSES.has(raw);
};

const aggregateClients = (rows) => {
  const groups = new Map();

  (rows || []).forEach((row) => {
    const email = normalizeEmail(row.requester_email);
    if (!email) return;

    if (!groups.has(email)) {
      groups.set(email, []);
    }
    groups.get(email).push(row);
  });

  return Array.from(groups.entries())
    .map(([email, bookings]) => {
      const sorted = [...bookings].sort((a, b) => bookingSortTime(b) - bookingSortTime(a));
      const latest = sorted[0] || {};
      const hasActiveBooking = sorted.some((booking) => !isInactiveBookingStatus(booking));

      return {
        id: email,
        email,
        full_name: String(latest.requester_name || "").trim() || "Unknown",
        phone: String(latest.requester_phone || "").trim(),
        recent_booking: String(latest.event_name || latest.request_code || "").trim() || "-",
        total_bookings: sorted.length,
        status: hasActiveBooking ? "Active" : "Inactive",
        bookings: sorted,
      };
    })
    .sort((a, b) => bookingSortTime(b.bookings[0]) - bookingSortTime(a.bookings[0]));
};

const statusLabel = (value) => {
  if (value === "in_review") return "In Review";
  if (value === "initial_consultation") return "Initial Consultation";
  if (value === "proposal_contract" || value === "proposal_quotation" || value === "agreement_confirmation" || value === "contract_signing") {
    return "Proposal & Contract";
  }
  if (value === "event_preparation") return "Event Preparation";
  if (value === "event_live") return "Posted on Live Event";
  if (value === "accepted" || value === "approved") return "Event Preparation";
  if (value === "declined") return "Declined";
  if (value === "cancelled") return "Cancelled";
  return String(value || "in_review").charAt(0).toUpperCase() + String(value || "in_review").slice(1);
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", year: "numeric" });
};

const bookingTypeLabel = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "corporate") return "Corporate";
  if (raw === "concert") return "Concert";
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "-";
};

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, request_code, requester_name, requester_email, requester_phone, event_name, booking_type, status, project_stage, created_at, updated_at")
        .order("updated_at", { ascending: false });

      if (error) {
        console.error("Failed to load clients:", error);
        setClients([]);
        setLoadError("Could not load clients. Check that you can read bookings.");
        setLoading(false);
        return;
      }

      setClients(aggregateClients(data || []));
      setLoadError("");
      setLoading(false);
    };

    fetchClients();

    const channel = supabase
      .channel("admin-clients-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings" }, fetchClients)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const filteredClients = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return clients.filter((client) => {
      const matchStatus = statusFilter === "All" || client.status === statusFilter;
      const matchSearch =
        !needle ||
        String(client.full_name || "").toLowerCase().includes(needle) ||
        String(client.email || "").toLowerCase().includes(needle) ||
        String(client.recent_booking || "").toLowerCase().includes(needle);

      return matchStatus && matchSearch;
    });
  }, [clients, search, statusFilter]);

  const totalPages = totalPagesFromCount(filteredClients.length, PAGE_SIZE);
  const paginatedClients = slicePage(filteredClients, currentPage, PAGE_SIZE);
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (!selectedClient) return;
    const next = clients.find((client) => client.id === selectedClient.id);
    if (next) {
      setSelectedClient(next);
    }
  }, [clients, selectedClient]);

  const emptyMessage = loading && clients.length === 0
    ? "Loading..."
    : clients.length === 0
    ? "No clients yet."
    : "No clients match this search or status filter.";

  return (
    <div className={adminPageBg}>
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
          <h1 className={adminPageTitle}>
            Clients
          </h1>

          <div className={adminSearchWrap}>
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder="Search..."
              className={adminSearchInput}
            />
          </div>
        </div>

        <div className={`max-w-7xl mx-auto ${adminCard}`} ref={listTopRef}>
          {loadError ? (
            <p className="mb-4 text-sm text-red-600">{loadError}</p>
          ) : null}

          <BusyTable show={showSkeleton} tone="admin">
          <div className={adminTableWrap}>
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className={adminTableHead}>
                  <th className="px-4 py-3 text-left">Client Name</th>
                  <th className="px-4 py-3 text-left">Recent Booking</th>
                  <th className="px-4 py-3 text-center">Total Bookings</th>
                  <th className="px-4 py-3 text-center relative">
                    <div className="inline-flex items-center gap-1 relative">
                      <span>Status</span>
                      <select
                        value={statusFilter}
                        onChange={(event) => {
                          setStatusFilter(event.target.value);
                        }}
                        className="absolute inset-0 opacity-0 cursor-pointer text-gray-700"
                      >
                        <option value="All">All</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      <span className="text-white">▾</span>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center">View</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.length === 0 ? (
                  <tr>
                    <td colSpan="5" className={adminEmpty}>
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  paginatedClients.map((client) => (
                    <tr key={client.id} className={adminRow}>
                      <td className={adminTdName}>
                        {client.full_name || "Unknown"}
                      </td>
                      <td className={adminTd}>
                        {client.recent_booking}
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-gray-600">
                        {client.total_bookings}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span
                          className={`${adminStatusPill} ${
                            client.status === "Inactive"
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {client.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedClient(client)}
                          className={adminPrimaryBtn}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </BusyTable>

          <AdminPagination
            className="mt-6"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      </div>

      {selectedClient ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-[92vw] sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-lg relative p-6">
            <button
              type="button"
              onClick={() => setSelectedClient(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>

            <h2 className="text-sm font-semibold mb-4 text-gray-900 pr-8">
              Client Details: {selectedClient.full_name}
            </h2>

            <div className="flex flex-col items-center mb-6">
              <div className="w-14 h-14 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-bold mb-2">
                {selectedClient.full_name?.charAt(0).toUpperCase() || "C"}
              </div>
              <p className="font-semibold text-gray-900">{selectedClient.full_name}</p>
            </div>

            <div className="space-y-3 text-sm">
              <Detail label="Full Name" value={selectedClient.full_name} />
              <Detail label="Email" value={selectedClient.email || "-"} />
              <Detail label="Phone" value={selectedClient.phone || "-"} />
              <Detail label="Total Bookings" value={selectedClient.total_bookings} />
              <Detail label="Status" value={selectedClient.status} />
            </div>

            <div className="mt-6">
              <h3 className="text-xs font-semibold mb-2 text-gray-700">
                Bookings
              </h3>
              {selectedClient.bookings.length === 0 ? (
                <div className="bg-gray-100 rounded text-xs p-2 text-gray-500">
                  No bookings.
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedClient.bookings.map((booking) => {
                    const uiStatus = deriveAdminUiStatus(booking);
                    return (
                      <button
                        type="button"
                        key={booking.id}
                        onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                        className="w-full text-left rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 hover:bg-gray-100"
                      >
                        <p className="text-xs font-semibold text-gray-900">
                          {booking.request_code || "Booking"}
                          {booking.event_name ? ` · ${booking.event_name}` : ""}
                        </p>
                        <p className="mt-1 text-[11px] text-gray-500">
                          {bookingTypeLabel(booking.booking_type)} · {statusLabel(uiStatus)} · {formatDisplayDate(booking.updated_at || booking.created_at)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <input
      value={value}
      disabled
      className="w-full border rounded px-2 py-1 text-sm bg-gray-100 text-gray-800"
    />
  </div>
);

export default Clients;
